import { Accessor } from "solid-js";

export type TooltipPlacement = 'top' | 'left' | 'bottom' | 'right';

export class TooltipOptions {
    tooltip: string;
    placement: TooltipPlacement = 'top';

    constructor(tooltip: string) {
        this.tooltip = tooltip;
    }
}

// Prepare the tooltip directive
declare module "solid-js" {
    namespace JSX {
        interface Directives {
            tooltip: string | TooltipOptions;
        }
    }
}

declare global {
    interface Window {
        tooltip: (el: HTMLButtonElement, params: Accessor<string | TooltipOptions>) => void;
    }
}

// Create the tooltip div
const tooltipElement = document.createElement('div');
tooltipElement.classList.add('st-tooltip');

const tooltipArrow = document.createElement('span');
tooltipArrow.classList.add('tooltip-arrow');
tooltipElement.appendChild(tooltipArrow);

const tooltipContent = document.createElement('div');
tooltipContent.classList.add('tooltip-content');
tooltipElement.appendChild(tooltipContent);

// If the current element we are tooltiping is removed from the dom, detach the tooltip
let currentElement: HTMLElement | undefined = undefined;
setInterval(() => {
    if (currentElement && tooltipElement.isConnected && !currentElement.isConnected && document.body.contains(tooltipElement)) {
        currentElement = undefined;
        document.body.removeChild(tooltipElement);
    }
}, 250);

window.tooltip = window.tooltip ?? ((el: HTMLButtonElement, params: Accessor<TooltipOptions | string>) => {

    el.addEventListener('mouseenter', () => {
        if (!params()) return;
        let options = params();
        if (typeof options === 'string') {
            options = new TooltipOptions(options);
        }

        currentElement = el;

        tooltipContent.innerText = options.tooltip;
        tooltipElement.style.minWidth = '';
        tooltipElement.style.opacity = '0';

        const rootElement = el.closest('dialog') || document.body;
        rootElement.appendChild(tooltipElement);

        repositionTooltip(el, options);
        tooltipElement.style.opacity = '';
    })

    el.addEventListener('mouseleave', () => {
        const rootElement = el.closest('dialog') || document.body;
        if (rootElement?.contains(tooltipElement)) {
            rootElement.removeChild(tooltipElement);
        }
    });

    el.addEventListener('mousedown', () => {
        tooltipElement.style.opacity = '0';
    });
});

function repositionTooltip(el: HTMLElement, options: TooltipOptions) {
    const rootElement = el.closest('dialog') || document.body;

    if (tooltipElement && currentElement) {
        const bodyRect = rootElement.getBoundingClientRect();
        const elRect = currentElement.getBoundingClientRect();

        tooltipElement.setAttribute('data-placement', options.placement);
        tooltipElement.style.left = tooltipElement.style.top = tooltipElement.style.bottom = tooltipElement.style.right = '';

        // Set the default position
        if (options.placement === 'top' || options.placement === 'bottom') {
            tooltipElement.style.minWidth = elRect.width + 'px';
            const ttRect = tooltipElement.getBoundingClientRect();
            const hcenter_offset = (elRect.width - ttRect.width) / 2;

            tooltipElement.style.left = (elRect.x + hcenter_offset) + 'px';
            if (options.placement === 'top') {
                tooltipElement.style.top = (elRect.top - ttRect.height - 6) + 'px';
            }
            else if (options.placement === 'bottom') {
                tooltipElement.style.top = elRect.bottom + 'px';
            }

            // If we are outside the screen, set the right position
            if (tooltipElement.getBoundingClientRect().right > bodyRect.right) {
                tooltipElement.style.left = '';
                tooltipElement.style.right = (bodyRect.right - elRect.right) + 'px';
            }

            // Check if we should render above the button
            if (tooltipElement.getBoundingClientRect().bottom > bodyRect.bottom) {
                tooltipElement.setAttribute('data-placement', 'top');
                tooltipElement.style.top = (elRect.top - ttRect.height) + 'px';
            }
        }
        else {
            tooltipElement.style.minWidth = '';
            const ttRect = tooltipElement.getBoundingClientRect();
            const vcenter_offset = (elRect.height - ttRect.height) / 2;
            tooltipElement.style.top = (elRect.top + vcenter_offset) + 'px';

            if (options.placement === 'right') {
                tooltipElement.style.left = elRect.right + 'px';
            }
            else if (options.placement === 'left') {
                tooltipElement.style.left = (elRect.left - ttRect.width) + 'px';
            }

            // If we are outside the screen, set the right position
            if (tooltipElement.getBoundingClientRect().right > bodyRect.right) {
                tooltipElement.setAttribute('data-placement', 'left');
                tooltipElement.style.left = (elRect.left - ttRect.width) + 'px';
            }
            else if (tooltipElement.getBoundingClientRect().left < bodyRect.left) {
                tooltipElement.setAttribute('data-placement', 'right');
                tooltipElement.style.left = elRect.right + 'px';
            }
        }
    }
}
